import type { FC } from 'react';
import { InlineNotification } from '@carbon/react';
import styles from './pre-drill-workspace-content.module.scss';

// Friendly text for target-zone generation failure codes — basin-config gaps
// (no zones for the permit's basin), not FE errors.
const GENERATION_FAILURE_TEXT: Record<string, string> = {
  no_basin_match:
    'No matching basin is configured for this permit, so the agent could not suggest a target zone.',
  no_candidates_for_basin:
    'This permit’s basin has no candidate zones configured, so the agent could not suggest a target zone.',
};

interface ContentBannersProps {
  confirmFailed: boolean;
  clearFailed: boolean;
  /** Set when the geometry is read-only: why, or null for the default promoted reason. */
  readOnlyReason: string | null | undefined;
  geometryEditFailed: boolean;
  onDismissGeometryEditFailure: () => void;
  datumUpdateError: string | null;
  onDismissDatumUpdateFailure: () => void;
  generationError: { code?: string; message?: string } | null;
}

// The editor-wide banner stack above the sections, moved out of the host
// (PreDrillWorkspaceContent) so the host stays within its size budget.
export const ContentBanners: FC<ContentBannersProps> = ({
  confirmFailed,
  clearFailed,
  readOnlyReason,
  geometryEditFailed,
  onDismissGeometryEditFailure,
  datumUpdateError,
  onDismissDatumUpdateFailure,
  generationError,
}) => (
  <>
    {confirmFailed && (
      <InlineNotification
        kind="error"
        title="Failed to confirm target zone"
        lowContrast
      />
    )}
    {clearFailed && (
      <InlineNotification
        kind="error"
        title="Failed to clear the target zone selection"
        lowContrast
      />
    )}
    {readOnlyReason !== undefined && (
      <InlineNotification
        className={styles.banner}
        kind="info"
        lowContrast
        hideCloseButton
        title="This wellbore geometry is read-only."
        subtitle={
          readOnlyReason ??
          'The workspace has been promoted, so its geometry can be read but not changed.'
        }
      />
    )}
    {geometryEditFailed && (
      <InlineNotification
        className={styles.banner}
        kind="error"
        title="Failed to save the wellbore geometry"
        lowContrast
        onCloseButtonClick={onDismissGeometryEditFailure}
      />
    )}
    {datumUpdateError && (
      <InlineNotification
        className={styles.banner}
        kind="error"
        lowContrast
        title="Failed to save the property details"
        subtitle={datumUpdateError}
        onCloseButtonClick={onDismissDatumUpdateFailure}
      />
    )}
    {generationError && (
      <InlineNotification
        kind="warning"
        lowContrast
        hideCloseButton
        title="No target zone could be generated"
        subtitle={
          (generationError.code &&
            GENERATION_FAILURE_TEXT[generationError.code]) ||
          generationError.message ||
          'The agent run did not produce a target zone.'
        }
      />
    )}
  </>
);
